import snakeCase from 'lodash/snakeCase'

import { instance } from '@/api/instances'
import { addMemedLog } from '@/components/healthPlan/utils/addLog'
import { LogLevel } from '@/wrappers/logger'

const fetchTasks = async (store, { personId, type, signal }) => {
  try {
    const selectedFilters = { ...store.state.selectedFilters }
    //Search for only one type (when open healthPlan task)
    selectedFilters.type = [type]
    const tasks = await getTasks(selectedFilters, personId, signal)

    store.commit('setHealthPlanTasks', tasks)
  } catch (error) {
    store.dispatch(
      'errors/setError',
      { error, request: 'pillars/healthPlan/fetchFilteredTasks' },
      { root: true }
    )
  }
}

const getTasks = async (selectedFilters, personId, signal) => {
  const params = handleFilterParams(selectedFilters)
  const tasks = await instance.get(`persons/${personId}/task?${params}`, {
    signal
  })

  return tasks
}

const fetchTaskCounters = async (store, signal) => {
  try {
    //Get counter for all types
    const selectedFilters = { ...store.state.selectedFilters }
    selectedFilters.type = []
    const params = handleFilterParams(selectedFilters)
    const counters = await instance.get(
      `persons/${store.rootState.pillars.personId}/task/counters?${params}`,
      { signal }
    )

    store.commit('setTaskCounters', counters)
  } catch (error) {
    store.dispatch(
      'errors/setError',
      { error, request: 'pillars/healthPlan/fetchTaskCounters' },
      { root: true }
    )
  }
}

const fetchTasksCounterByDemand = async (store, demand) => {
  if (!demand.caseId) {
    return 0
  }
  try {
    const counters = await instance.get(
      `persons/${store.rootState.pillars.personId}/task/counters?status=ACTIVE&case_id=${demand.caseId}`
    )

    if (!counters) return 0
    return Object.keys(counters).reduce(
      (total, key) => (total += counters[key]),
      0
    )
  } catch (error) {
    store.dispatch(
      'errors/setError',
      { error, request: 'pillars/healthPlan/fetchTasksCounterByDemand' },
      { root: true }
    )
    return 0
  }
}

const handleFilterParam = (param, values) => {
  if (!values.length) return ''
  return `${snakeCase(param)}=${values.join(`&${param}=`)}`
}

const handleFilterParams = selectedFilters => {
  const params = [
    handleFilterParam('status', selectedFilters.status),
    handleFilterParam(
      'releasedBy',
      selectedFilters.releasedBy.map(staff => staff.id)
    ),
    handleFilterParam(
      'group',
      selectedFilters.group.map(group => group.id)
    ),
    handleFilterParam('type', selectedFilters.type),
    handleFilterParam(
      'case_id',
      selectedFilters.demand.map(demand => demand.caseId)
    )
  ]

  return params.filter(Boolean).join('&')
}

const fetchFilteredTasks = async (store, { personId, signal }) => {
  const person_id = personId || store.rootState.pillars.personId
  try {
    store.dispatch('fetchTaskCounters')

    const tasks = await getTasks(store.state.selectedFilters, person_id, signal)

    store.commit('setHealthPlanTasks', tasks)
  } catch (error) {
    store.dispatch(
      'errors/setError',
      { error, request: 'pillars/healthPlan/fetchFilteredTasks' },
      { root: true }
    )
  }
}

const createTask = async (store, task) => {
  try {
    const normalizedTask = await instance.post(
      `persons/${task.personId}/task`,
      {
        json: task
      }
    )

    return normalizedTask
  } catch (error) {
    store.dispatch(
      'errors/setError',
      { error, request: 'pillars/healthPlan/createTask' },
      { root: true }
    )
    return false
  }
}

const createMemedTask = async (store, task) => {
  addMemedLog(LogLevel.INFO, 'Generating task from memed prescription')

  try {
    const createdTask = await instance.post(`prescriptions/v2`, {
      json: task
    })

    addMemedLog(LogLevel.INFO, 'Generated task from memed prescription', {
      taskId: createdTask.id
    })

    return createdTask
  } catch (error) {
    addMemedLog(
      LogLevel.ERROR,
      'Error generating task from memed prescription',
      {
        errorCode: error.code,
        errorMessage: error.message,
        errorStatus: error.response?.status,
        errorData: error.response?.data
      }
    )

    store.dispatch(
      'errors/setError',
      { error, request: 'pillars/healthPlan/createTask' },
      { root: true }
    )
    return null
  }
}

const updateTask = async (store, task) => {
  try {
    const normalizedTask = await instance.put(
      `persons/${task.personId}/task/${task.id}`,
      {
        json: task
      }
    )

    return normalizedTask
  } catch (error) {
    store.dispatch(
      'errors/setError',
      { error, request: 'pillars/healthPlan/updateTask' },
      { root: true }
    )
    return false
  }
}

const removeTask = async (store, task) => {
  try {
    return await instance.delete(`persons/${task.personId}/task/${task.id}`)
  } catch (error) {
    store.dispatch(
      'errors/setError',
      { error, request: 'pillars/healthPlan/removeTask' },
      { root: true }
    )
  }
}

const setFilter = (store, filter) => {
  store.commit('setFilter', filter)
}

const clearFilters = (store, task) => {
  store.commit('clearFilters', task)
}

const searchTaskTemplates = async (store, { query, type, signal }) => {
  try {
    return await instance.get(`task_templates/?q=${query}&type=${type}`, {
      signal
    })
  } catch (error) {
    store.dispatch(
      'errors/setError',
      { error, request: 'pillars/healthPlan/searchTaskTemplates' },
      { root: true }
    )
    return []
  }
}

const updateTypeFilter = (store, type) => {
  const filter = [...store.state.selectedFilters.type]
  if (filter.includes(type)) {
    const index = filter.findIndex(t => t === type)
    filter.splice(index, 1)
  } else {
    filter.push(type)
  }
  store.commit('setFilter', { filterType: 'type', filterValue: filter })
}

export default {
  fetchFilteredTasks,
  createTask,
  updateTask,
  searchTaskTemplates,
  clearFilters,
  fetchTaskCounters,
  setFilter,
  updateTypeFilter,
  fetchTasks,
  removeTask,
  fetchTasksCounterByDemand,
  createMemedTask
}
